import * as styles from "./QuestionWithAnswer.module.css";
import { useState, useEffect } from "react";
import { getAnswer } from "../../api/answer";
import ReactMarkdown from "react-markdown";
import { MdOutlineThumbDown, MdOutlineThumbUp, MdThumbUp, MdThumbDown } from "react-icons/md";
import { IoInformationCircleOutline } from "react-icons/io5";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";


export default function QuestionWithAnswer({
  question,
  answer,
  shouldStream,
  onClick,
  onStreamComplete,
  productLink,
  questionId,
  onAddAnswer,
  feedback: existingFeedback,
  onFeedback,
  customerId,
  sessionId,
  website,
  productNumber,
  questionHistory,
  popUpMessage,
  setFocusedQuestionIndex,
  answeredQuestions
}) {
  const [streamedText, setStreamedText] = useState("");
  const [loading, setLoading] = useState(false);
  const [showFeedbackButtons, setShowFeedbackButtons] = useState(!existingFeedback);
  const [showPopup, setShowPopup] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);

  useEffect(() => {
    setShowFeedbackButtons(!existingFeedback);
  }, [questionId, existingFeedback]);

  useEffect(() => {
    // Check if screen width is mobile size
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!isMobile) return; // Only add click outside listener for mobile

    const handleClickOutside = (e) => {
      if (e.target.closest(`.${styles.answerFooterIcon}`)) return;
      setShowPopup(false);
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMobile]);

  const handleInfoClick = (e) => {
    if (isMobile) {
      e.stopPropagation();
      setShowPopup(prev => !prev);
    }
  };

  const fetchAnswer = async () => {
    try {
      setLoading(true);
      const response = await getAnswer(productLink, question, customerId, sessionId, website, productNumber, questionHistory);
      let accumulatedText = "";
      setLoading(false);
      for await (const chunk of response) {
        if (chunk === "[RESPONSE_COMPLETE]") break;
        accumulatedText += chunk;
        setStreamedText(accumulatedText);
      }

      onStreamComplete();
      onAddAnswer(questionId, accumulatedText);
    } catch (error) {
      console.error("Error fetching answer:", error);
      setStreamedText("Sorry, there was an error getting the answer.");
    }
  };

  useEffect(() => {
    if (!answer && shouldStream) {
      setStreamedText("");
      fetchAnswer();
      return;
    }

    let isActive = true;

    if (shouldStream) {
      setStreamedText("");
      let index = 0;
      const streamInterval = setInterval(() => {
        if (!isActive) return;

        if (index <= answer.length) {
          setStreamedText(answer.slice(0, index));
          index++;
        } else {
          clearInterval(streamInterval);
          setTimeout(() => {
            if (isActive && onStreamComplete) {
              onStreamComplete();
            }
          }, 100);
        }
      }, 10);

      return () => {
        isActive = false;
        clearInterval(streamInterval);
      };
    } else if (answer) {
      setStreamedText(answer);
    }
  }, [shouldStream, answer]);

  const handleFeedbackClick = (type) => {
    onFeedback(questionId, type);
    setShowFeedbackButtons(false);
    setShowThankYou(true);
    setTimeout(() => {
      setShowThankYou(false);
    }, 1500);
  };

  useEffect(() => {
    console.log("showThankYou updated:", showThankYou);
  }, [showThankYou]);

  return (
    <div className={styles.container}>
      <div className={styles.contentWrapper}>
        <div className={styles.questionContainer}>
          {!isMobile && answeredQuestions?.length > 1 && (
            <button 
              className={styles.navButton}
              disabled={answeredQuestions.findIndex(q => q.id === questionId) === 0}
              onClick={() => {
                const currentIndex = answeredQuestions.findIndex(q => q.id === questionId);
                const newIndex = currentIndex > 0 ? currentIndex - 1 : currentIndex;
                if (newIndex !== currentIndex) {
                  setFocusedQuestionIndex(newIndex);
                }
              }}
            >
              <IoIosArrowBack />
            </button>
          )}
          <div className={styles.questionText} onClick={onClick}>
            {question}
          </div>
          {!isMobile && answeredQuestions?.length > 1 && (
            <button 
              className={styles.navButton}
              disabled={answeredQuestions.findIndex(q => q.id === questionId) === answeredQuestions.length - 1}
              onClick={() => {
                const currentIndex = answeredQuestions.findIndex(q => q.id === questionId);
                const newIndex = currentIndex + 1;
                if (newIndex < answeredQuestions.length) {
                  setFocusedQuestionIndex(newIndex);
                }
              }}
            >
              <IoIosArrowForward />
            </button>
          )}
        </div>
        {loading && (
          <div className={styles.loaderContainer}>
            <div className={styles.loader}></div>
          </div>
        )}
        {!loading && (
          <div className={styles.answerText}>
            <ReactMarkdown>{streamedText}</ReactMarkdown>
            <div className={styles.answerFooter}>
              <div className={styles.leftContent}>
                <p className={styles.aiGeneratedText}>AI-generert</p>
                <IoInformationCircleOutline 
                  className={styles.answerFooterIcon}
                  onClick={handleInfoClick}
                  onMouseEnter={() => !isMobile && setShowPopup(true)}
                  onMouseLeave={() => !isMobile && setShowPopup(false)}
                />
              </div>
              <div className={styles.feedbackContainer}>
                {showFeedbackButtons && (
                  <>
                    <div
                      className={`${styles.feedbackButton} ${styles.feedbackButtonUp}`}
                      onClick={() => handleFeedbackClick('up')}
                    >
                      {existingFeedback === 'up' ? <MdThumbUp /> : <MdOutlineThumbUp />}
                    </div>
                    <div
                      className={`${styles.feedbackButton} ${styles.feedbackButtonDown}`}
                      onClick={() => handleFeedbackClick('down')}
                    >
                      {existingFeedback === 'down' ? <MdThumbDown /> : <MdOutlineThumbDown />}
                    </div>
                  </>
                )}
                {showThankYou && (
                  <div className={styles.thankYouMessage}>Takk!</div>
                )}
              </div>
            </div>
            {showPopup && (
              <div className={styles.popup}>
                {popUpMessage}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
